/** @fileoverview Displays the app preview iframe and its navigation controls. */

import {
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Globe,
  Monitor,
  RotateCw,
  Settings2,
  ClipboardList,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import type { ConnectedSystem } from '../../services/systemsApi';
import type { FabrikAppDefinition } from '../../types/apps';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import DataConnectionsSettings from './DataConnectionsSettings';
import { setPreviewSnapshotRequester, type PreviewSelectionItem } from '../../lib/previewSelection';

type PreviewVersion = 'draft' | 'published';

type Props = {
  appId: string;
  appName: string;
  appSlug: string;
  onRenameApp: (name: string, preserveOldUrl: boolean) => Promise<void>;
  onDefinitionChange: (definition: FabrikAppDefinition) => void;
  previewUrl: string;
  draftPreviewUrl: string;
  publishedPreviewUrl: string;
  publishedVersionNo?: number;
  previewOpen: boolean;
  previewFrameRef: RefObject<HTMLIFrameElement | null>;
  view: 'preview' | 'settings';
  onViewChange: (view: 'preview' | 'settings') => void;
  tenantId: string;
  tenantName: string;
  appDefinition?: FabrikAppDefinition;
  publishedDefinition?: FabrikAppDefinition;
  systems: ConnectedSystem[];
  planAvailable?: boolean;
  appDraftRevision: number;
  planWorkspace?: ReactNode;
  onSystemChange: (
    version: 'draft' | 'published',
    sourceSystemId: string,
    targetSystemId: string
  ) => Promise<void>;
};

const COPIED_RESET_MS = 1800;

/** How long the chat waits for the preview to report its selection before giving up. */
const SNAPSHOT_TIMEOUT_MS = 1500;

export default function LivePreviewPane({
  appId,
  appName,
  appSlug,
  onRenameApp,
  onDefinitionChange,
  previewUrl,
  draftPreviewUrl,
  publishedPreviewUrl,
  publishedVersionNo,
  previewOpen,
  previewFrameRef,
  view,
  onViewChange,
  tenantId,
  tenantName,
  appDefinition,
  publishedDefinition,
  systems,
  onSystemChange,
  planAvailable = false,
  appDraftRevision,
  planWorkspace,
}: Props) {
  const [previewVersion, setPreviewVersion] = useState<PreviewVersion>('draft');
  const [copied, setCopied] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const selectionModeRef = useRef(false);
  const snapshotResolvers = useRef<
    Array<{
      id: string;
      timer: number;
      resolve: (items: PreviewSelectionItem[]) => void;
      reject: (error: Error) => void;
    }>
  >([]);
  const snapshotRequestId = useRef(0);
  const copiedTimerRef = useRef<number | undefined>(undefined);
  const publishedAvailable = Boolean(publishedPreviewUrl);
  const [showPlan, setShowPlan] = useState(false);
  const initialPlanAutoOpened = useRef(false);
  const previousAppId = useRef(appId);
  const previousAppDraftRevision = useRef(appDraftRevision);
  const viewLabel = showPlan ? 'Plan' : view === 'preview' ? 'Live preview' : 'Settings';

  useEffect(() => {
    if (!publishedAvailable) setPreviewVersion('draft');
  }, [publishedAvailable]);

  useEffect(() => {
    if (previousAppDraftRevision.current === appDraftRevision) return;
    previousAppDraftRevision.current = appDraftRevision;
    setShowPlan(false);
    onViewChange('preview');
  }, [appDraftRevision, onViewChange]);

  useEffect(() => {
    if (previousAppId.current !== appId) {
      previousAppId.current = appId;
      initialPlanAutoOpened.current = false;
      setShowPlan(false);
    }
    if (!planAvailable) setShowPlan(false);
  }, [appId, planAvailable]);
  useEffect(() => {
    const onPlanUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ appId?: unknown; revision?: unknown }>).detail;
      if (
        detail?.appId === appId &&
        typeof detail.revision === 'number' &&
        !initialPlanAutoOpened.current
      ) {
        initialPlanAutoOpened.current = true;
        setShowPlan(true);
      }
    };
    window.addEventListener('fabrik:app-plan-updated', onPlanUpdated);
    return () => window.removeEventListener('fabrik:app-plan-updated', onPlanUpdated);
  }, [appId]);

  useEffect(() => () => window.clearTimeout(copiedTimerRef.current), []);

  const selectedVersion =
    previewVersion === 'published' && publishedAvailable ? 'published' : 'draft';
  const selectedAddressUrl =
    selectedVersion === 'published' ? publishedPreviewUrl : draftPreviewUrl;
  const selectedIframeUrl = selectedVersion === 'published' ? publishedPreviewUrl : previewUrl;
  const iframeKey =
    selectedVersion === 'published'
      ? `published-${publishedVersionNo}-${refreshCount}`
      : `draft-${refreshCount}`;
  const displayedUrl = selectedAddressUrl.replace(/^https?:\/\//, '');

  useEffect(() => {
    const frame = previewFrameRef.current;
    if (!frame || view !== 'preview' || showPlan || !previewOpen) return;
    const pendingResolvers = snapshotResolvers.current;
    const onMessage = (event: MessageEvent<unknown>) => {
      if (
        event.source !== frame.contentWindow ||
        event.origin !== window.location.origin ||
        !event.data ||
        typeof event.data !== 'object'
      )
        return;
      const message = event.data as {
        type?: string;
        appId?: string;
        items?: PreviewSelectionItem[];
        requestId?: string;
        error?: string;
      };
      if (message.appId !== appId) return;
      if (message.type === 'fabrik:preview-ready') {
        frame.contentWindow?.postMessage(
          { type: 'fabrik:preview-selection-mode', appId, enabled: selectionModeRef.current },
          window.location.origin
        );
        return;
      }
      if (message.type === 'fabrik:preview-selection-send') {
        window.dispatchEvent(new CustomEvent('fabrik:preview-selection-send'));
        return;
      }
      if (message.type === 'fabrik:preview-selection-error') {
        const request = pendingResolvers.find(({ id }) => id === message.requestId);
        if (request) {
          pendingResolvers.splice(pendingResolvers.indexOf(request), 1);
          window.clearTimeout(request.timer);
          request.reject(new Error(message.error || 'Could not refresh the preview selection.'));
        }
        window.dispatchEvent(
          new CustomEvent('fabrik:preview-selection-error', {
            detail: message.error || 'Selected preview data is too large.',
          })
        );
        return;
      }
      if (message.type !== 'fabrik:preview-selection' || !Array.isArray(message.items)) return;
      const items = message.items;
      const request = pendingResolvers.find(({ id }) => id === message.requestId);
      if (request) {
        pendingResolvers.splice(pendingResolvers.indexOf(request), 1);
        window.clearTimeout(request.timer);
        request.resolve(items);
      }
      window.dispatchEvent(new CustomEvent('fabrik:preview-selection', { detail: items }));
    };
    const onRemove = (event: Event) => {
      const path = (event as CustomEvent<string>).detail;
      if (typeof path === 'string') {
        frame.contentWindow?.postMessage(
          { type: 'fabrik:preview-selection-remove', appId, path },
          window.location.origin
        );
      }
    };
    const onFocus = (event: Event) => {
      const path = (event as CustomEvent<string>).detail;
      if (typeof path === 'string') {
        frame.contentWindow?.postMessage(
          { type: 'fabrik:preview-selection-focus', appId, path },
          window.location.origin
        );
      }
    };
    const onClear = () =>
      frame.contentWindow?.postMessage(
        { type: 'fabrik:preview-selection-clear', appId },
        window.location.origin
      );
    const onToggleSelection = (event: Event) => {
      if (selectedVersion !== 'draft') return;
      // The toggle may carry an explicit target state, e.g. { enabled: false } once a message is sent.
      const requested = (event as CustomEvent<{ enabled?: boolean } | undefined>).detail?.enabled;
      const enabled = requested ?? !selectionModeRef.current;
      if (enabled === selectionModeRef.current) return;
      selectionModeRef.current = enabled;
      frame.contentWindow?.postMessage(
        { type: 'fabrik:preview-selection-mode', appId, enabled },
        window.location.origin
      );
      window.dispatchEvent(
        new CustomEvent('fabrik:preview-selection-mode-changed', { detail: enabled })
      );
    };
    window.addEventListener('message', onMessage);
    window.addEventListener('fabrik:preview-selection-clear', onClear);
    window.addEventListener('fabrik:preview-selection-remove', onRemove);
    window.addEventListener('fabrik:preview-selection-focus', onFocus);
    window.addEventListener('fabrik:preview-selection-toggle', onToggleSelection);
    const releaseSnapshotRequester = setPreviewSnapshotRequester(
      () =>
        new Promise((resolve, reject) => {
          if (selectedVersion !== 'draft') {
            resolve([]);
            return;
          }
          const id = String(++snapshotRequestId.current);
          const request = {
            id,
            timer: 0,
            resolve,
            reject,
          };
          request.timer = window.setTimeout(() => {
            const index = pendingResolvers.indexOf(request);
            if (index < 0) return;
            pendingResolvers.splice(index, 1);
            reject(new Error('Could not refresh the preview selection. Please try again.'));
          }, SNAPSHOT_TIMEOUT_MS);
          pendingResolvers.push(request);
          frame.contentWindow?.postMessage(
            { type: 'fabrik:preview-selection-snapshot', appId, requestId: id },
            window.location.origin
          );
        })
    );
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('fabrik:preview-selection-clear', onClear);
      window.removeEventListener('fabrik:preview-selection-remove', onRemove);
      window.removeEventListener('fabrik:preview-selection-focus', onFocus);
      window.removeEventListener('fabrik:preview-selection-toggle', onToggleSelection);
      pendingResolvers.splice(0).forEach(({ reject, timer }) => {
        window.clearTimeout(timer);
        reject(new Error('Preview selection was reset.'));
      });
      releaseSnapshotRequester();
    };
  }, [appId, previewFrameRef, selectedVersion, iframeKey, view, showPlan, previewOpen]);

  useEffect(() => {
    selectionModeRef.current = false;
    previewFrameRef.current?.contentWindow?.postMessage(
      { type: 'fabrik:preview-selection-mode', appId, enabled: false },
      window.location.origin
    );
    window.dispatchEvent(new CustomEvent('fabrik:preview-selection', { detail: [] }));
    window.dispatchEvent(new CustomEvent('fabrik:preview-selection-clear'));
    window.dispatchEvent(
      new CustomEvent('fabrik:preview-selection-mode-changed', { detail: false })
    );
  }, [appId, previewFrameRef, selectedVersion, iframeKey, view, showPlan, previewOpen]);

  const copySelectedUrl = async () => {
    try {
      await navigator.clipboard.writeText(selectedAddressUrl);
      setCopied(true);
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className={`relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-app-surface-muted transition-opacity duration-300 ease-in-out motion-reduce:transition-none ${
        previewOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      aria-hidden={!previewOpen}
      inert={!previewOpen}
    >
      <section className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-app-surface-muted p-3 sm:p-5">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface-card shadow-sm">
          <div className="flex h-10 shrink-0 items-center gap-3 border-b border-app-border bg-app-surface-card px-3">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-2 rounded-md border-0 bg-transparent px-0 py-0 text-xs font-normal text-app-text-muted hover:bg-transparent hover:text-app-text-muted aria-expanded:bg-transparent"
                    aria-label={`Current view: ${viewLabel}. Choose view`}
                  >
                    {showPlan ? (
                      <ClipboardList
                        className="h-3.5 w-3.5 text-app-brand-strong"
                        aria-hidden="true"
                      />
                    ) : view === 'preview' ? (
                      <Monitor className="h-3.5 w-3.5 text-app-brand-strong" aria-hidden="true" />
                    ) : (
                      <Settings2 className="h-3.5 w-3.5 text-app-brand-strong" aria-hidden="true" />
                    )}
                    {viewLabel}
                    <ChevronDown className="h-3.5 w-3.5 text-app-text-muted" aria-hidden="true" />
                  </Button>
                }
              />
              <DropdownMenuContent align="start" className="min-w-40">
                <DropdownMenuItem
                  onClick={() => {
                    setShowPlan(false);
                    onViewChange('preview');
                  }}
                >
                  <Monitor className="text-app-brand-strong" aria-hidden="true" />
                  Live preview
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setShowPlan(false);
                    onViewChange('settings');
                  }}
                >
                  <Settings2 className="text-app-brand-strong" aria-hidden="true" />
                  Settings
                </DropdownMenuItem>
                {planAvailable && (
                  <DropdownMenuItem onClick={() => setShowPlan(true)}>
                    <ClipboardList className="text-app-brand-strong" aria-hidden="true" />
                    Plan
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex min-w-0 flex-1 justify-center">
              <div className="flex h-8 w-full max-w-xl min-w-0 items-center">
                {publishedAvailable ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      type="button"
                      className="relative z-10 flex h-8 shrink-0 items-center justify-between gap-1.5 rounded-l-full rounded-r-none border border-app-border bg-app-surface-card px-3.5 text-xs font-medium text-app-text-muted shadow-none hover:bg-app-surface-muted hover:text-app-text focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 data-popup-open:rounded-tl-xl data-popup-open:rounded-tr-none data-popup-open:rounded-b-none"
                      aria-label="Preview mode"
                    >
                      <Globe className="h-3.5 w-3.5 text-app-text-muted" aria-hidden="true" />
                      <span>{selectedVersion === 'published' ? 'Live' : 'Draft'}</span>
                      <ChevronDown className="h-3.5 w-3.5 text-app-text-muted" aria-hidden="true" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      side="bottom"
                      sideOffset={0}
                      align="start"
                      className="min-w-0 rounded-t-none rounded-bl-xl rounded-br-none border border-t-0 border-app-border bg-app-surface-card p-0 text-app-text shadow-sm ring-0"
                    >
                      {selectedVersion === 'published' ? (
                        <DropdownMenuItem
                          className="h-8 w-full rounded-none px-3 py-0 text-sm font-normal"
                          onClick={() => setPreviewVersion('draft')}
                        >
                          <span>Draft</span>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          className="h-8 w-full rounded-none px-3 py-0 text-sm font-normal"
                          onClick={() => setPreviewVersion('published')}
                        >
                          <span>Live</span>
                          {publishedVersionNo != null && (
                            <span className="ml-auto shrink-0 text-xs font-normal text-app-text-muted">
                              v{publishedVersionNo}
                            </span>
                          )}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div
                    className="flex h-8 shrink-0 items-center gap-1.5 rounded-l-full rounded-r-none border border-app-border bg-app-surface-card px-3.5 text-xs font-medium text-app-text-muted"
                    aria-label="Preview mode: Draft"
                  >
                    <Globe className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Draft</span>
                  </div>
                )}
                <div className="-ml-px flex h-8 min-w-0 flex-1 items-center overflow-hidden rounded-l-none rounded-r-full border border-app-border bg-app-surface-muted-strong">
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="size-7 shrink-0 rounded-none border-0 bg-transparent p-0 text-app-text-secondary hover:bg-transparent hover:text-app-text focus-visible:border-0 focus-visible:bg-transparent"
                          aria-label="Refresh preview"
                          onClick={() => setRefreshCount((count) => count + 1)}
                        >
                          <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      }
                    />
                    <TooltipContent side="bottom">Refresh preview</TooltipContent>
                  </Tooltip>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={`min-w-0 flex-1 justify-start overflow-hidden rounded-none border-0 bg-transparent pl-0 text-xs font-normal text-app-text-secondary hover:bg-app-surface-muted hover:text-app-text ${
                      copied ? 'text-app-brand-strong hover:text-app-brand-strong' : ''
                    }`}
                    title={copied ? 'Copied preview URL' : 'Copy preview URL'}
                    aria-label={copied ? 'Preview URL copied' : 'Copy preview URL'}
                    onClick={() => void copySelectedUrl()}
                  >
                    <span className="min-w-0 flex-1 truncate">{displayedUrl}</span>
                    {copied ? (
                      <Check
                        className="h-3.5 w-3.5 shrink-0 animate-in fade-in zoom-in-50 duration-200 motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    ) : (
                      <Copy
                        className="h-3.5 w-3.5 shrink-0 animate-in fade-in duration-200 motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    )}
                    <span className="sr-only" aria-live="polite">
                      {copied ? 'Copied preview URL' : ''}
                    </span>
                  </Button>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="size-7 rounded-none border-0 bg-transparent p-0 text-app-text-secondary hover:bg-transparent hover:text-app-text focus-visible:border-0 focus-visible:bg-transparent"
                          aria-label={`Open ${
                            selectedVersion === 'published' ? 'Live' : 'Draft'
                          } preview in a new tab`}
                          onClick={() =>
                            window.open(selectedAddressUrl, '_blank', 'noopener,noreferrer')
                          }
                        >
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      }
                    />
                    <TooltipContent side="bottom">Open preview in a new tab</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          {showPlan && planWorkspace ? (
            planWorkspace
          ) : view === 'preview' ? (
            <iframe
              key={iframeKey}
              ref={previewFrameRef}
              className="block h-0 min-h-0 w-full flex-1 border-0 bg-app-surface-card"
              src={selectedIframeUrl}
              title="Live app preview"
            />
          ) : view === 'settings' ? (
            <DataConnectionsSettings
              tenantId={tenantId}
              tenantName={tenantName}
              appId={appId}
              appName={appName}
              appSlug={appSlug}
              isAppPublished={publishedVersionNo != null}
              onRenameApp={onRenameApp}
              definition={selectedVersion === 'published' ? publishedDefinition : appDefinition}
              onDefinitionChange={onDefinitionChange}
              version={selectedVersion}
              systems={systems}
              onSystemChange={async (sourceSystemId, targetSystemId) => {
                await onSystemChange(selectedVersion, sourceSystemId, targetSystemId);
                if (selectedVersion === 'published') {
                  setRefreshCount((count) => count + 1);
                }
              }}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}
